import { useState, useEffect } from 'react';
import { Plus, Calendar, FileText, Scale } from 'lucide-react';
import BalanceSheetStatement from '../components/BalanceSheet/BalanceSheetStatement';
import api from '../services/api';
import toast from 'react-hot-toast';

interface BSData {
  id: string;
  periodEnd: string;
  format: string;
  scenario: string;
  totals: {
    totalAssets: number;
    totalLiabilities: number;
    totalEquity: number;
  };
  createdAt: string;
}

const BalanceSheet = () => {
  const [balanceSheets, setBalanceSheets] = useState<BSData[]>([]);
  const [selectedBalanceSheet, setSelectedBalanceSheet] = useState<string | null>(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Form state
  const [periodEnd, setPeriodEnd] = useState('');
  const [priorPeriodEnd, setPriorPeriodEnd] = useState('');
  const [format, setFormat] = useState<'classified' | 'comparative' | 'common_size'>('classified');
  const [scenario, setScenario] = useState<'actual' | 'budget' | 'forecast'>('actual');
  const [includeComparative, setIncludeComparative] = useState(false);

  useEffect(() => {
    loadBalanceSheets();
  }, []);

  const loadBalanceSheets = async () => {
    setLoading(true);
    try {
      const response = await api.get('/balance-sheet/statements', {
        params: { limit: 20 },
      });
      setBalanceSheets(response.data.data);

      // Auto-select the most recent balance sheet
      if (response.data.data.length > 0 && !selectedBalanceSheet) {
        setSelectedBalanceSheet(response.data.data[0].id);
      }
    } catch (error: any) {
      toast.error('Failed to load balance sheets');
      console.error('Error loading balance sheets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBalanceSheet = async () => {
    if (!periodEnd) {
      toast.error('Please select period end date');
      return;
    }

    if (includeComparative && !priorPeriodEnd) {
      toast.error('Please select prior period end date for comparative analysis');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/balance-sheet/generate', {
        periodEnd,
        priorPeriodEnd: includeComparative ? priorPeriodEnd : undefined,
        format,
        scenario,
      });

      toast.success('Balance sheet generated successfully!');
      setShowGenerateModal(false);

      // Reload balance sheets and select the new one
      await loadBalanceSheets();
      setSelectedBalanceSheet(response.data.data.id);

      // Reset form
      setPeriodEnd('');
      setPriorPeriodEnd('');
      setFormat('classified');
      setScenario('actual');
      setIncludeComparative(false);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to generate balance sheet');
      console.error('Error generating balance sheet:', error);
    } finally {
      setLoading(false);
    }
  };

  const getQuickDate = (offset: 'current' | 'last_month' | 'last_quarter' | 'last_year') => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();

    let date: Date;

    switch (offset) {
      case 'current':
        date = today;
        break;
      case 'last_month':
        date = new Date(year, month - 1, 0); // Last day of previous month
        break;
      case 'last_quarter':
        const quarter = Math.floor(month / 3);
        date = new Date(year, quarter * 3, 0); // Last day of previous quarter
        break;
      case 'last_year':
        date = new Date(year - 1, 11, 31); // Last day of previous year
        break;
    }

    return date!.toISOString().split('T')[0];
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 bg-white border-b border-neutral-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-neutral-900">Balance Sheet</h1>
            <p className="text-neutral-600 mt-1">
              View financial position with assets, liabilities, and equity
            </p>
          </div>

          <button onClick={() => setShowGenerateModal(true)} className="btn btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Generate Balance Sheet
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - Balance Sheet List */}
        <div className="w-80 bg-white border-r border-neutral-200 overflow-y-auto">
          <div className="p-4">
            <h3 className="font-semibold text-neutral-900 mb-4">Recent Balance Sheets</h3>

            {loading && balanceSheets.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="spinner"></div>
              </div>
            ) : balanceSheets.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
                <p className="text-sm text-neutral-600">No balance sheets yet</p>
                <button
                  onClick={() => setShowGenerateModal(true)}
                  className="text-sm text-primary-600 hover:text-primary-700 mt-2"
                >
                  Generate your first balance sheet
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {balanceSheets.map((bs) => (
                  <div
                    key={bs.id}
                    onClick={() => setSelectedBalanceSheet(bs.id)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedBalanceSheet === bs.id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-neutral-600 capitalize">
                        {bs.format} · {bs.scenario}
                      </span>
                      {selectedBalanceSheet === bs.id && (
                        <span className="text-xs bg-primary-600 text-white px-2 py-0.5 rounded">
                          Current
                        </span>
                      )}
                    </div>

                    <div className="flex items-center text-sm text-neutral-700 mb-2">
                      <Calendar className="w-3 h-3 mr-1" />
                      <span>
                        As of{' '}
                        {new Date(bs.periodEnd).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                    </div>

                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-neutral-600">Assets:</span>
                        <span className="font-medium text-neutral-900">
                          {formatCurrency(bs.totals.totalAssets)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-neutral-600">Liabilities:</span>
                        <span className="font-medium text-alert-600">
                          {formatCurrency(bs.totals.totalLiabilities)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-neutral-600">Equity:</span>
                        <span className="font-medium text-success-600">
                          {formatCurrency(bs.totals.totalEquity)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Main Content - Balance Sheet Display */}
        <div className="flex-1 p-6 bg-neutral-50 overflow-y-auto">
          <BalanceSheetStatement
            balanceSheetId={selectedBalanceSheet || undefined}
            onGenerate={() => setShowGenerateModal(true)}
          />
        </div>
      </div>

      {/* Generate Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="p-6 border-b border-neutral-200">
              <h2 className="text-xl font-bold text-neutral-900">Generate Balance Sheet</h2>
              <p className="text-sm text-neutral-600 mt-1">
                Select period and format to generate a balance sheet
              </p>
            </div>

            <div className="p-6 space-y-6">
              {/* Quick Date Buttons */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Quick Select Period End
                </label>
                <div className="grid grid-cols-4 gap-2">
                  <button
                    onClick={() => setPeriodEnd(getQuickDate('current'))}
                    className="btn btn-secondary text-sm"
                  >
                    Today
                  </button>
                  <button
                    onClick={() => setPeriodEnd(getQuickDate('last_month'))}
                    className="btn btn-secondary text-sm"
                  >
                    Last Month
                  </button>
                  <button
                    onClick={() => setPeriodEnd(getQuickDate('last_quarter'))}
                    className="btn btn-secondary text-sm"
                  >
                    Last Quarter
                  </button>
                  <button
                    onClick={() => setPeriodEnd(getQuickDate('last_year'))}
                    className="btn btn-secondary text-sm"
                  >
                    Last Year
                  </button>
                </div>
              </div>

              {/* Period End */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Period End Date
                </label>
                <input
                  type="date"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  className="input w-full"
                />
              </div>

              {/* Comparative Analysis Toggle */}
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={includeComparative}
                    onChange={(e) => setIncludeComparative(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm font-medium text-neutral-700">
                    Include Comparative Analysis (Prior Period)
                  </span>
                </label>
              </div>

              {/* Prior Period End */}
              {includeComparative && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Prior Period End Date
                  </label>
                  <input
                    type="date"
                    value={priorPeriodEnd}
                    onChange={(e) => setPriorPeriodEnd(e.target.value)}
                    className="input w-full"
                  />
                </div>
              )}

              {/* Format Selection */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Balance Sheet Format
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {(['classified', 'comparative', 'common_size'] as const).map((fmt) => (
                    <button
                      key={fmt}
                      onClick={() => setFormat(fmt)}
                      className={`px-4 py-3 rounded-lg border text-sm font-medium transition-colors ${
                        format === fmt
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-neutral-300 bg-white text-neutral-700 hover:border-neutral-400'
                      }`}
                    >
                      <div className="font-semibold capitalize">
                        {fmt.replace('_', ' ')}
                      </div>
                      <div className="text-xs mt-1 text-neutral-600">
                        {fmt === 'classified'
                          ? 'Current/Non-current'
                          : fmt === 'comparative'
                          ? 'Period comparison'
                          : 'Percentage format'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Scenario */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Scenario
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {(['actual', 'budget', 'forecast'] as const).map((scen) => (
                    <button
                      key={scen}
                      onClick={() => setScenario(scen)}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        scenario === scen
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-neutral-300 bg-white text-neutral-700 hover:border-neutral-400'
                      }`}
                    >
                      {scen.charAt(0).toUpperCase() + scen.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-neutral-200 flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowGenerateModal(false)}
                className="btn btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateBalanceSheet}
                className="btn btn-primary"
                disabled={loading || !periodEnd}
              >
                {loading ? (
                  <>
                    <div className="spinner w-4 h-4 mr-2"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <Scale className="w-4 h-4 mr-2" />
                    Generate Balance Sheet
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BalanceSheet;
